import * as React from 'react';
import { ResistorDivider } from 'js-electrical-engineering-equations';
import { useCallback, useState } from 'react';
import { Button, Card, CardActions, CardContent, TextField } from '@material-ui/core';
import { useLastCalculated } from '../../hooks/useLastCalculated';

const fieldsDefaults = {
  inputVoltage: '',
  resistor1: '',
  resistor2: '',
  outputVoltage: '',
};

export function ResistorDividerComponent(): JSX.Element {
  const [fields, setFields] = useState(fieldsDefaults);

  const { lastCalculated, wasLastCalculated, setLastCalculated } = useLastCalculated<
    keyof typeof fieldsDefaults
  >();

  const clear = useCallback(() => {
    setLastCalculated([]);
    setFields(fieldsDefaults);
  }, [setLastCalculated, setFields]);

  const onChange = useCallback(
    (event) => {
      const field = event.target.name;
      const value = event.target.value;
      setFields({ ...fields, [field]: value });
    },
    [fields, setFields],
  );

  const calculate = useCallback(
    (e) => {
      e.preventDefault();
      const { inputVoltage, resistor1, resistor2, outputVoltage } = fields;

      if (
        wasLastCalculated('outputVoltage') ||
        (inputVoltage && resistor1 && resistor2 && outputVoltage === '')
      ) {
        setFields({
          ...fields,
          ...ResistorDivider.calcOutputVoltage(
            parseFloat(inputVoltage),
            parseFloat(resistor1),
            parseFloat(resistor2),
          ),
        });
        setLastCalculated(['outputVoltage']);
      } else if (
        wasLastCalculated('resistor1') ||
        (inputVoltage && resistor2 && outputVoltage && resistor1 === '')
      ) {
        setFields({
          ...fields,
          ...ResistorDivider.calcResistor1(
            parseFloat(inputVoltage),
            parseFloat(outputVoltage),
            parseFloat(resistor2),
          ),
        });
        setLastCalculated(['resistor1']);
      } else if (
        wasLastCalculated('resistor2') ||
        (inputVoltage && resistor1 && outputVoltage && resistor2 === '')
      ) {
        setFields({
          ...fields,
          ...ResistorDivider.calcResistor2(
            parseFloat(inputVoltage),
            parseFloat(outputVoltage),
            parseFloat(resistor1),
          ),
        });
        setLastCalculated(['resistor2']);
      } else if (
        wasLastCalculated('inputVoltage') ||
        (outputVoltage && resistor1 && resistor2 && inputVoltage === '')
      ) {
        setFields({
          ...fields,
          ...ResistorDivider.calcInputVoltage(
            parseFloat(outputVoltage),
            parseFloat(resistor1),
            parseFloat(resistor2),
          ),
        });
        setLastCalculated(['inputVoltage']);
      }
    },
    [fields, lastCalculated, wasLastCalculated],
  );

  return (
    <form onSubmit={calculate}>
      <div>
        <Card>
          <CardContent>
            <div>
              <p>Supply at least three of the values to calculate the fourth.</p>
              <p>
                The base equation for a resistor divider network is <br /> Vo = (Vs * R2) / (R1 +
                R2)
              </p>
              <TextField
                label="Input Voltage"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('inputVoltage')}
                fullWidth
                name="inputVoltage"
                margin="normal"
                onChange={onChange}
                value={fields.inputVoltage}
              />
              <TextField
                label="Resistor 1 (Ohms)"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('resistor1')}
                fullWidth
                name="resistor1"
                margin="normal"
                onChange={onChange}
                value={fields.resistor1}
              />
              <TextField
                label="Resistor 2 (Ohms)"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('resistor2')}
                fullWidth
                name="resistor2"
                margin="normal"
                onChange={onChange}
                value={fields.resistor2}
              />
              <TextField
                label="Output Voltage"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={wasLastCalculated('outputVoltage')}
                fullWidth
                name="outputVoltage"
                margin="normal"
                onChange={onChange}
                value={fields.outputVoltage}
              />
            </div>
          </CardContent>
          <CardActions>
            <Button type="button" tabIndex="6" onClick={clear}>
              Clear
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              style={{ marginLeft: 'auto' }}
            >
              Calculate
            </Button>
          </CardActions>
        </Card>
      </div>
    </form>
  );
}
